import { useState } from "react";
import {
  Sparkles,
  ShoppingBag,
  Loader2,
  Quote,
  Leaf,
  Heart,
  Palette,
  Scissors,
  Shirt,
  Award,
} from "lucide-react";
import { useCheckout } from "@/lib/useCheckout";

const SIZES = [
  { id: "tshirt", label: "S / M", price: 22, note: "Small – Medium" },
  { id: "tshirt-l", label: "L", price: 25, note: "Large", featured: true },
  { id: "tshirt-xl", label: "XL+", price: 30, note: "XL and up" },
];

const pillars = [
  {
    icon: Palette,
    title: "Hand Spray-Painted",
    body: "Every shirt is spray-painted by hand in signature green — the color of growth, life, and renewal.",
  },
  {
    icon: Shirt,
    title: "100% Premium Cotton",
    body: "Exceptional comfort. Trusted durability. Made to move with you.",
  },
  {
    icon: Sparkles,
    title: "One of a Kind",
    body: "No two shirts are exactly alike. Each one is a wearable work of art.",
  },
  {
    icon: Heart,
    title: "Made with Intention",
    body: "Every shirt is created with creativity and faith — the heart of the 414 brand.",
  },
];

export default function Tshirts() {
  const [size, setSize] = useState(SIZES[0]);
  const { buy, loadingId } = useCheckout();

  return (
    <div data-testid="tshirts-page" className="bg-brand-cream">
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden pt-16 lg:pt-24 pb-24 lg:pb-32">
        <img
          src="/assets/flower_leaf.png"
          alt=""
          aria-hidden="true"
          className="absolute -top-10 -left-20 w-72 opacity-70 pointer-events-none hidden lg:block"
        />
        <img
          src="/assets/flower_fern.png"
          alt=""
          aria-hidden="true"
          className="absolute bottom-10 -right-16 w-72 opacity-70 pointer-events-none hidden lg:block"
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
            <span
              className="inline-block bg-brand-moss text-brand-gold px-6 py-1.5 text-xs uppercase tracking-[0.35em] -rotate-1"
              data-testid="tshirts-eyebrow"
            >
              414, LLC Signature Tees
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-moss leading-tight">
              Every T-shirt
              <br />
              <span className="italic text-[#B7574E]">tells a story.</span>
            </h1>
            <p className="mt-6 text-lg text-brand-ink/75 leading-relaxed">
              Original. Handcrafted. Created from the heart &mdash; making every piece as unique as
              the person wearing it.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* LEFT: Big product image */}
            <div
              data-testid="tshirt-hero-card"
              className="relative bg-gradient-to-br from-[#E8EEDA] to-[#F1F5E4] rounded-[2.5rem] overflow-hidden border border-brand-moss/10 shadow-lg"
            >
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-brand-parchment">
                <img
                  src="/assets/tshirt_front_back.png"
                  alt="414 LLC Rooted in Faith Tee — front and back"
                  className="w-full h-full object-cover"
                />
                <span className="absolute top-5 left-5 inline-flex items-center gap-2 bg-brand-moss text-brand-gold px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] rounded-full shadow">
                  <Sparkles className="w-3.5 h-3.5" />
                  Hand Spray-Painted
                </span>
                <span className="absolute bottom-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-brand-cream/90 backdrop-blur-sm text-brand-moss px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] rounded-full shadow">
                  Front &middot; Back
                </span>
              </div>

              <div className="p-8 lg:p-10">
                <h2 className="font-serif text-3xl lg:text-4xl text-brand-moss leading-tight">
                  Rooted in Faith Tee
                </h2>
                <p className="mt-2 text-sm uppercase tracking-[0.3em] text-brand-moss/60">
                  Handcrafted &middot; Custom-Made &middot; One of a Kind
                </p>

                <div className="mt-6 flex items-baseline gap-3">
                  <span className="font-serif text-5xl text-brand-moss">${size.price}</span>
                  <span className="text-xs uppercase tracking-[0.25em] text-brand-ink/60">
                    / Size {size.label}
                  </span>
                </div>

                {/* Size selector */}
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-[0.3em] text-brand-moss/60 mb-3">
                    Choose your size
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {SIZES.map((s) => {
                      const active = s.id === size.id;
                      return (
                        <button
                          key={s.id}
                          data-testid={`size-${s.id}-btn`}
                          onClick={() => setSize(s)}
                          className={`relative px-3 py-3 rounded-2xl border-2 transition-all duration-300 text-center ${
                            active
                              ? "border-brand-moss bg-brand-moss text-brand-gold shadow-md"
                              : "border-brand-moss/20 bg-white/60 text-brand-moss hover:border-brand-moss/50"
                          }`}
                        >
                          <div className="font-serif text-xl leading-none">{s.label}</div>
                          <div
                            className={`mt-1 text-[10px] uppercase tracking-[0.2em] ${
                              active ? "text-brand-gold/90" : "text-brand-moss/60"
                            }`}
                          >
                            ${s.price}
                          </div>
                          {s.featured && (
                            <span className="absolute -top-2 -right-2 bg-[#B7574E] text-brand-cream text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full">
                              Popular
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  type="button"
                  data-testid={`buy-${size.id}-btn`}
                  onClick={() => buy(size.id, `Rooted in Faith Tee ${size.label}`)}
                  disabled={loadingId === size.id}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-moss text-brand-gold px-8 py-4 text-xs uppercase tracking-[0.35em] rounded-full hover:bg-brand-moss/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
                >
                  {loadingId === size.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Opening checkout&hellip;
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4" strokeWidth={1.8} />
                      Add to Cart &middot; ${size.price}
                    </>
                  )}
                </button>

                <p className="mt-4 text-center text-[11px] uppercase tracking-[0.3em] text-brand-moss/60">
                  Each shirt is one of a kind &middot; slight variations are the signature
                </p>
              </div>
            </div>

            {/* RIGHT: Founder story */}
            <div data-testid="tshirt-story" className="lg:pt-4">
              <span className="inline-block text-xs uppercase tracking-[0.35em] text-[#B7574E]">
                <Quote className="inline w-4 h-4 mr-1 -mt-1" />
                A note from the founder
              </span>
              <h3 className="mt-4 font-serif text-3xl lg:text-4xl text-brand-moss leading-snug">
                A wearable work of
                <br />
                <span className="italic text-[#B7574E]">art, faith &amp; growth.</span>
              </h3>

              <div className="mt-8 space-y-4 text-brand-ink/85 leading-relaxed">
                <p>
                  At <span className="font-serif italic text-brand-moss">414, LLC</span>, every
                  T-shirt tells a story. Each design is original, handcrafted, and created from the
                  heart, making every piece as unique as the person wearing it.
                </p>
                <p>
                  Our shirts are made from <strong>100% premium cotton</strong> for exceptional
                  comfort and durability. Using my favorite color &mdash; green, a symbol of growth,
                  life, and renewal &mdash; I carefully apply a custom spray-paint technique by hand
                  to create an organic, one-of-a-kind design. No two shirts are exactly alike,
                  ensuring that each one is a wearable work of art.
                </p>
                <p>
                  Every shirt is made with intention, creativity, and faith, reflecting the heart of
                  our brand:
                </p>
                <p className="font-serif text-2xl italic text-brand-moss pl-5 border-l-4 border-brand-gold">
                  Rooted in Faith. Growing with Purpose.
                </p>
                <p>
                  Whether you&rsquo;re expressing your individuality or supporting a small business
                  built on passion and purpose, your 414, LLC T-shirt is more than apparel &mdash;
                  it&rsquo;s a statement of originality, craftsmanship, and inspiration.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 bg-white/70 border border-brand-moss/15 px-4 py-2 rounded-full text-xs uppercase tracking-[0.25em] text-brand-moss">
                  <Scissors className="w-3.5 h-3.5" />
                  Handcrafted
                </span>
                <span className="inline-flex items-center gap-2 bg-white/70 border border-brand-moss/15 px-4 py-2 rounded-full text-xs uppercase tracking-[0.25em] text-brand-moss">
                  <Palette className="w-3.5 h-3.5" />
                  Custom-Made
                </span>
                <span className="inline-flex items-center gap-2 bg-white/70 border border-brand-moss/15 px-4 py-2 rounded-full text-xs uppercase tracking-[0.25em] text-brand-moss">
                  <Award className="w-3.5 h-3.5" />
                  One of a Kind
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 4 PILLARS ============== */}
      <section
        data-testid="tshirt-pillars"
        className="relative bg-brand-moss text-brand-cream py-24 lg:py-32 overflow-hidden"
      >
        <img
          src="/assets/flower_leaf.png"
          alt=""
          aria-hidden="true"
          className="absolute top-8 right-8 w-40 opacity-30 pointer-events-none hidden md:block"
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block bg-brand-gold text-brand-moss px-6 py-1.5 text-xs uppercase tracking-[0.35em] rotate-1">
              The Difference
            </span>
            <h2 className="mt-6 font-serif text-4xl lg:text-5xl leading-tight">
              Not printed. Not mass-made.
              <br />
              <span className="italic text-brand-gold">Personally poured.</span>
            </h2>
          </div>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, idx) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`tshirt-pillar-${idx + 1}`}
                  className="relative bg-brand-cream/5 backdrop-blur-sm border border-brand-gold/20 rounded-3xl p-8 hover:bg-brand-cream/10 transition-all duration-500 hover:-translate-y-1"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-gold text-brand-moss">
                    <Icon className="w-5 h-5" strokeWidth={1.7} />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-brand-cream leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-brand-cream/75 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== CLOSING BAND ============== */}
      <section
        data-testid="tshirt-closing"
        className="relative bg-brand-parchment py-20 overflow-hidden border-y border-brand-moss/10"
      >
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {["Handcrafted.", "Custom-Made.", "One of a Kind."].map((word, i, arr) => (
              <span key={word} className="inline-flex items-center gap-6">
                <span className="font-serif text-3xl md:text-4xl lg:text-5xl text-brand-moss italic">
                  {word}
                </span>
                {i < arr.length - 1 && (
                  <Leaf className="w-5 h-5 text-brand-gold hidden md:inline" />
                )}
              </span>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
            {SIZES.map((s) => (
              <button
                key={`cta-${s.id}`}
                type="button"
                data-testid={`cta-buy-${s.id}-btn`}
                onClick={() => {
                  setSize(s);
                  buy(s.id, `Rooted in Faith Tee ${s.label}`);
                }}
                disabled={loadingId === s.id}
                className="inline-flex items-center gap-2 bg-brand-moss text-brand-gold px-6 py-3 text-xs uppercase tracking-[0.3em] rounded-full hover:bg-brand-moss/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 shadow"
              >
                {loadingId === s.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4" />
                    {s.label} &middot; ${s.price}
                  </>
                )}
              </button>
            ))}
          </div>

          <p className="mt-8 text-brand-ink/75 max-w-xl mx-auto leading-relaxed">
            Prices: <span className="font-medium">$22 S/M</span> &middot;{" "}
            <span className="font-medium">$25 L</span> &middot;{" "}
            <span className="font-medium">$30 XL &amp; up</span>
          </p>
        </div>
      </section>
    </div>
  );
}
